import { SaleBidder, SaleContactBidder } from "./BidderData"
import { AgTechIndex, Index } from "@agtech/core/utilities/AgTechUtilities";
import { SaleContactDataGetAllContactsQuery } from "../operation/contacts/SaleContactDataQueries";
import { AgTechQueryRun, AgTechQueryWithActions, useAgTechQuery, useAgTechQueryCombination, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { useCurrentSale } from "../sales/SaleDataStore";
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface";
import { AgTechContact } from "@agtech/data/contacts/ContactData";

///
/// DTOs
///

export type SaleBidderDTO = {
    id: number,
    bidderNumber: string,
    contactId?: number
}

export type SaleBidderData = {
    bidders: AgTechIndex<SaleBidder>,
    contacts: AgTechIndex<AgTechContact>
}

export type BidderListDetails = {
    id: number,
    bidderNumber: string,
    bidderName?: string,
    contactId?: number
}

///
/// Queries
///

export const BidderDataGetSaleBiddersQuery: AgTechQueryWithActions<{ saleId: number }, AgTechIndex<SaleBidderDTO>, BidderQueryActions> = {
    queryKey: ({ saleId }) => ['SaleBidders', saleId],
    query: async (props) => {
        return await props.http.getEntity<SaleBidderDTO[], AgTechIndex<SaleBidderDTO>>({
            request: 'Bidder/' + props.inputData.saleId,
            convert: bidders => Index(bidders, b => b.id)
        });
    },
    content: {
        loadingText: 'Loading bidders...',
        failureText: 'Failed to load bidders.'
    },
    actions: {
        writeBidder: (existingBidders, bidder) => {
            return {
                ...existingBidders,
                [bidder.id]: {
                    id: bidder.id,
                    bidderNumber: bidder.bidderNumber!,
                    contactId: bidder.contact?.id
                }
            };
        },
        deleteBidder: (existingBidders, bidderId) => {
            const newBidders = { ...existingBidders };
            delete newBidders[bidderId];
            return newBidders;
        }
    }
}

type BidderQueryActions = {
    writeBidder: (existingBidders: AgTechIndex<SaleBidderDTO>, bidder: SaleBidder) => AgTechIndex<SaleBidderDTO>,
    deleteBidder: (existingBidders: AgTechIndex<SaleBidderDTO>, bidderId: number) => AgTechIndex<SaleBidderDTO>
}

///
/// Composite Queries
///

export const useBidderDataGetSaleBidderDataQuery = (): AgTechQueryRun<SaleBidderData> => {
    const currentSale = useCurrentSale();

    return useAgTechQueryCombination({
        firstQuery: {
            query: BidderDataGetSaleBiddersQuery,
            queryContext: { saleId: currentSale.id }
        },
        secondQuery: {
            query: SaleContactDataGetAllContactsQuery
        },
        projection: (bidderDtos, contacts) => {
            const bidders = Object.values(bidderDtos).map<SaleBidder>(bidderDTO => ({
                id: bidderDTO.id,
                bidderNumber: bidderDTO.bidderNumber,
                contact: bidderDTO.contactId ? contacts[bidderDTO.contactId] : undefined
            }));

            return {
                bidders: Index(bidders, b => b.id),
                contacts: contacts
            };
        }
    });
}

export const useBidderDataGetContactBiddersQuery = () => {
    const contactsQuery = useAgTechQuery({
        query: SaleContactDataGetAllContactsQuery
    });

    const biddersQuery = useBidderDataGetSaleBidderDataQuery();

    return useAgTechQueryResultCombination({
        firstQuery: contactsQuery,
        secondQuery: biddersQuery,
        projection: (contactIndex, bidderData) => {
            const contacts = Object.values(contactIndex);
            const bidders = Object.values(bidderData.bidders);
            const biddersIndexedByContactId = Index(bidders, bd => bd.contact?.id ?? -1);

            const contactBidders = contacts.map<SaleContactBidder>(contact => {
                const bidder = biddersIndexedByContactId[contact.id];

                return {
                    ...contact,
                    bidder: bidder
                };
            });

            return Index(contactBidders, cb => cb.id);
        }
    });

}

/// Providers

export const SaleBiddersDataProvider = (props: { 
    children: (bidders: SaleBidderData) => JSX.Element
}) => {
    const bidderDataQuery = useBidderDataGetSaleBidderDataQuery();

    return (
        <AgTechQueryLoadingSurface query={bidderDataQuery}>
            {props.children}
        </AgTechQueryLoadingSurface>
    );
}